import React, { useCallback, useState, useRef, useEffect } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';

import { useAppStore, useWindowStore } from '../../stores';

import Modal from '../Modal/Modal';
import VirtualTourDetail from '../VirtualTour/VirtualTourDetail';
import FavoriteButton from '../Personalization/Favorite/FavoriteButton';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

const PRIORITY_LOAD_COUNT = 6; // Load first 6 images immediately

const MediaGalleryPageVirtualTourListItem = ( { item, index } ) => {
  const [ modalIsOpen, setModalIsOpen ] = useState( false );
  const [ imageLoaded, setImageLoaded ] = useState( false );
  const imgHolderRef = useRef( null );
  const placeholderRef = useRef( null );

  // Use shared IntersectionObserver, skip for first 6 items (priority loading)
  const isPriority = index < PRIORITY_LOAD_COUNT;
  const [containerRef, isVisible] = useIntersectionObserver(isPriority);

  const isMobile = useWindowStore((state) => state.isMobile);
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const settings = useAppStore((state) => state.settings);
  const isYourTour = settings?.platform === 'yt';

  const {
    uuid,
    title,
    coverImage,
    threeSixties,
  } = item;

  // Preload image when visible and not already loaded
  useEffect(() => {
    if (!isVisible || !coverImage || imageLoaded) return;

    const img = new Image();
    img.onload = () => {
      setImageLoaded(true);
      if (imgHolderRef.current && placeholderRef.current) {
        placeholderRef.current.classList.add('imgHolder__placeholder--hidden');
        imgHolderRef.current.classList.add('imgHolder--loaded');
      }
    };
    img.onerror = () => {
      setImageLoaded(true); // Mark as loaded even on error to prevent retries
      if (imgHolderRef.current && placeholderRef.current) {
        placeholderRef.current.classList.add('imgHolder__placeholder--hidden');
        imgHolderRef.current.classList.add('imgHolder--loaded');
      }
    };
    img.src = coverImage;
  }, [isVisible, coverImage, imageLoaded]);

  const openModal = useCallback( () => {
    setModalIsOpen( true );
  }, [] );

  const components = personalizationActive ? [ <FavoriteButton
    key={`favorite-${uuid}`}
    type="virtual_tour"
    uuid={uuid}
    hasShadow={false}
    className="detailHeader__favorite"
  /> ] : [];

  const headerData = {
    components,
  };

  return (
    <>
      <Modal
        modalClassName="modal--anchored"
        isOpen={modalIsOpen}
        handleModalHide={() => setModalIsOpen( false )}
        showBlur
        showCloseButton
        title={title}
        headerData={headerData}
      >

        <VirtualTourDetail
          id={null}
          uuid={threeSixties ? null : uuid}
          threeSixtiesData={threeSixties}
          showInstructions={!isYourTour}
          className={classNames( 'detailSection detailSection--virtualTour', {
            'modal__section--fullWidth': isMobile,
          } )}
        />

      </Modal>

      <div className="gridItem" ref={containerRef}>
        <button
          className="modalOpen gridItemTrigger"
          onClick={openModal}
          type="button"
        >
          <div>
            <div className="imgContainer">
              {coverImage && <div ref={placeholderRef} className="imgHolder__placeholder" />}
              <div
                ref={imgHolderRef}
                className="imgHolder"
                style={{
                  backgroundImage: isVisible && coverImage ? `url(${coverImage})` : 'none',
                }}
              />
              <h2 className="gridItemTitle type-display-xl">{title}</h2>
            </div>
          </div>
        </button>

        { personalizationActive && (
          <FavoriteButton
            type="virtual_tour"
            uuid={uuid}
            hasShadow={false}
            className="favoriteBtn-positioner"
          />
        )}

      </div>
    </>
  );
};

MediaGalleryPageVirtualTourListItem.propTypes = {
  item: propTypes.shape({
    uuid: propTypes.string,
    title: propTypes.string,
    coverImage: propTypes.string,
    threeSixties: propTypes.arrayOf(propTypes.shape({
      uuid: propTypes.string,
      type: propTypes.string,
      buttonText: propTypes.string,
      url: propTypes.string,
    })),
  }).isRequired,
  index: propTypes.number.isRequired,
};

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(MediaGalleryPageVirtualTourListItem);
